// Offline PINs — the Club Card scanner's PIN check when the tablet has no connection.
//
// PLAIN ENGLISH. Since 1 Oct 2026 every PIN is checked by the server, so a till with no
// connection couldn't check one, and the scanner's offline mode (stamps queue, the offline
// reward picker) never started. JJ, 13 Oct 2026: "pins should work offline because the
// system should know the staff pins" … "let them back into the scanner offline" … "we can
// just do pin, but track the offline pins somewhere in the command centre".
//
// So with no connection, the PIN always gets through, and two things happen:
//  1. The BOOK: this tablet keeps a small book of the PINs the server has said yes to HERE —
//     not the PINs themselves. Each is stretched (PBKDF2-SHA256, 100,000 rounds) with this
//     tablet's own random salt, and only the result is kept, next to the name. Offline, a
//     typed PIN is stretched the same way and looked up, so the till knows who it is
//     ("pin_offline") — or that it doesn't ("pin_unchecked").
//  2. The CHECK: the typed PIN is encrypted to the Club Card server's key (RSA-OAEP SHA-256;
//     only the server can read it) and goes with the queued stamp, redemption or scanner
//     unlock. When it reaches the server it is checked and thrown away, and the Command
//     Centre's "Offline PINs" list shows who it really was, or that it matched nobody.
//
// The book is deliberately NOT filled from the downloaded staff list (?action=pinsForLogin):
// that list is being retired (PIN_LEAK_STATUS.md, step 3), and this must keep working after.
//
// Honest limit: a 4-digit PIN has only 10,000 possibilities, so anyone who can run code on
// this tablet could test them all against the book. The stretching makes that slow (minutes,
// not milliseconds), and the book stays small — only PINs used on this tablet, each dropped
// after 14 days without a fresh server yes, or the moment the server refuses it. That is far
// less than the plain list every tablet holds today.
//
// WebCrypto (crypto.subtle) only exists on https and localhost, so on a plain-http LAN
// preview the book and the encryption are off (everything reads as unchecked/unverified).

export const BOOK_KEY = "rolled.offlinePins.v1";
export const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
export const MAX_ENTRIES = 80;
export const ITERATIONS = 100000;

// What the till sends with a stamp, redemption or unlock whose PIN the server could NOT
// check at the time (absent = the server checked it live, as usual).
export const AUTH_PIN_OFFLINE = "pin_offline";      // the PIN was in this tablet's book
export const AUTH_PIN_UNCHECKED = "pin_unchecked";  // not in the book; let through, checked later

// The Club Card server's public key for offline PINs (SPKI, base64). The private half is the
// OFFLINE_PIN_PRIVATE_KEY variable on the Club Card server; a new pair needs both changed
// (and the "v1" below bumped). Public by design — it can only encrypt.
export const SERVER_KEY_VERSION = "v1";
export const SERVER_PUBLIC_KEY_SPKI = "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAoZbVC3TRdyWEtQrvIsg0zSSN4uea9Mtm7lx7xyHmmFtof35rdryvbmeyYIvI5pso8/7OZQjdkIiIEZX3JeRd19Lht9sOzNsgUlt40FDnBh7jsEkO9P5uNmH8JAsRbwCM2JZqvNfOQjTBhRp2tKfQC5MLsFRiOYaP6xgcNWYlr50g4LrrxjK7NW8KOAd3iKTUr6KX3BoyAcTGAVcrJIaC/5M6o0pO2FYtmmKTxK+RtDVgkslZdlfjZrYLzX+1FlGQUNIXLsB3ZKL0F5De+a3pzwlAYGAOC6hIahhCTE22ZeeQfywBkrDOk0UKv1/2PyWpJ67JOnmZhoF2IjrgHO5GdQIDAQAB";

const enc = new TextEncoder();

function defaultDeps() {
  const g = globalThis;
  return {
    storage: (() => { try { return g.localStorage || null; } catch (e) { return null; } })(),
    now: () => Date.now(),
    subtle: g.crypto && g.crypto.subtle,
    randomBytes: (n) => g.crypto.getRandomValues(new Uint8Array(n)),
    iterations: ITERATIONS,
  };
}

function toB64(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function fromB64(s) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const isPin = (pin) => /^\d{4}$/.test(String(pin == null ? "" : pin));

function load(storage) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(BOOK_KEY);
    const b = raw ? JSON.parse(raw) : null;
    if (b && b.v === 1 && typeof b.salt === "string" && Number.isFinite(b.iter) && Array.isArray(b.entries)) return b;
  } catch (e) {}
  return null;
}
function save(storage, book) {
  if (!storage) return false;
  try { storage.setItem(BOOK_KEY, JSON.stringify(book)); return true; } catch (e) { return false; }
}

async function stretch(pin, saltB64, iterations, subtle) {
  const key = await subtle.importKey("raw", enc.encode(String(pin)), "PBKDF2", false, ["deriveBits"]);
  const bits = await subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt: fromB64(saltB64), iterations }, key, 256);
  return toB64(new Uint8Array(bits));
}

const fresh = (e, now) => e && typeof e.h === "string" && Number.isFinite(e.t) && now - e.t >= 0 && now - e.t < MAX_AGE_MS;

// Book writes are queued one after another, so two PIN checks landing together can't
// each save their own copy and lose the other's entry.
let chain = Promise.resolve();
function serial(fn) {
  const run = chain.then(fn, fn);
  chain = run.catch(() => {});
  return run;
}

// The server just said yes to this PIN on this tablet: keep (or refresh) its entry.
// Never throws; resolves true when saved.
export function remember(pin, who, deps) {
  const d = { ...defaultDeps(), ...(deps || {}) };
  const name = who && typeof who.name === "string" ? who.name.trim() : "";
  if (!isPin(pin) || !name || !d.subtle || !d.storage) return Promise.resolve(false);
  return serial(async () => {
    try {
      const now = d.now();
      let book = load(d.storage);
      if (!book) book = { v: 1, salt: toB64(d.randomBytes(16)), iter: d.iterations, entries: [] };
      const h = await stretch(pin, book.salt, book.iter, d.subtle);
      const keep = book.entries.filter((e) => e.h !== h && fresh(e, now));
      keep.unshift({ h, name: name.slice(0, 100), role: String((who && who.role) || "").slice(0, 40), t: now });
      book.entries = keep.slice(0, MAX_ENTRIES);
      return save(d.storage, book);
    } catch (e) { return false; }
  });
}

// The server definitely refused this PIN (wrong, switched off): drop it from the book.
export function forget(pin, deps) {
  const d = { ...defaultDeps(), ...(deps || {}) };
  if (!isPin(pin) || !d.subtle || !d.storage) return Promise.resolve(false);
  return serial(async () => {
    try {
      const book = load(d.storage);
      if (!book || !book.entries.length) return false;
      const h = await stretch(pin, book.salt, book.iter, d.subtle);
      const left = book.entries.filter((e) => e.h !== h);
      if (left.length === book.entries.length) return false;
      book.entries = left;
      return save(d.storage, book);
    } catch (e) { return false; }
  });
}

// Offline: who is this PIN on this tablet? {name, role} or null. Never throws.
export async function lookup(pin, deps) {
  const d = { ...defaultDeps(), ...(deps || {}) };
  if (!isPin(pin) || !d.subtle) return null;
  try {
    const book = load(d.storage);
    if (!book || !book.entries.length) return null;
    const now = d.now();
    const h = await stretch(pin, book.salt, book.iter, d.subtle);
    const e = book.entries.find((x) => x.h === h && fresh(x, now));
    return e ? { name: e.name, role: e.role || "" } : null;
  } catch (e) { return null; }
}

// How many people this tablet can check offline right now (for the Offline note).
export function size(deps) {
  const d = { ...defaultDeps(), ...(deps || {}) };
  const book = load(d.storage);
  if (!book) return 0;
  const now = d.now();
  return book.entries.filter((e) => fresh(e, now)).length;
}

// The typed PIN, encrypted so only the Club Card server can read it: "v1:" + base64 of
// RSA-OAEP-SHA256. null when it can't be done (no WebCrypto here, not a PIN) — the server
// then records the use as "unverified". Never throws. deps.publicKeySpki is for tests.
let importedKey = null;
export async function encryptPin(pin, deps) {
  const d = { ...defaultDeps(), ...(deps || {}) };
  if (!isPin(pin) || !d.subtle) return null;
  try {
    const spki = d.publicKeySpki || SERVER_PUBLIC_KEY_SPKI;
    let key = !d.publicKeySpki ? importedKey : null;
    if (!key) {
      key = await d.subtle.importKey("spki", fromB64(spki), { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
      if (!d.publicKeySpki) importedKey = key;
    }
    const ct = await d.subtle.encrypt({ name: "RSA-OAEP" }, key, enc.encode(String(pin)));
    return SERVER_KEY_VERSION + ":" + toB64(new Uint8Array(ct));
  } catch (e) { return null; }
}
